/**
 * product-custom.js
 * Handles: qty stepper, tab switcher, next-day-delivery date,
 *          VAT price, mobile swipe carousel, ATC/wishlist proxies
 *
 * Rules enforced:
 *  - Never submit or modify form#product-form directly
 *  - Never interfere with Shopify's variant selection JS
 *  - Never duplicate ATC form submissions
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     UTILITY: find the native Shopify qty input
     inside the product form (any naming variant)
     ───────────────────────────────────────────── */
  function getNativeQtyInput() {
    return document.querySelector(
      'form[id^="product-form"] input[name="quantity"],' +
      'form[id^="ProductForm"] input[name="quantity"],' +
      '.product-form__input input[name="quantity"],' +
      'quantity-input input[name="quantity"]'
    );
  }

  /* ─────────────────────────────────────────────
     UTILITY: get currently selected variant ID
     Checks URL param → native form input → JSON
     ───────────────────────────────────────────── */
  function getSelectedVariantId() {
    // 1. URL ?variant=
    var urlParam = new URLSearchParams(window.location.search).get('variant');
    if (urlParam) return urlParam;

    // 2. Native hidden form input (updated by theme JS on variant change)
    var nativeInput = document.querySelector(
      'form[id^="product-form"] input[name="id"],' +
      'form[id^="ProductForm"] input[name="id"],' +
      'form[action="/cart/add"] input[name="id"]'
    );
    if (nativeInput && nativeInput.value) return nativeInput.value;

    // 3. product-info element selected variant JSON
    var productInfo = document.querySelector('product-info');
    if (productInfo) {
      var sel = productInfo.querySelector('[data-selected-variant]');
      if (sel) {
        try { return JSON.parse(sel.textContent).id; } catch (e) {}
      }
    }

    return null;
  }

  /* ─────────────────────────────────────────────
     1. QUANTITY STEPPER
     Syncs our custom UI with Shopify's hidden qty
     input so the native ATC form always gets the
     right value.
     ───────────────────────────────────────────── */
  function initQtyStepper() {
    document.querySelectorAll('.cc-qty-stepper').forEach(function (stepper) {
      var minusBtn = stepper.querySelector('[data-action="minus"]');
      var plusBtn  = stepper.querySelector('[data-action="plus"]');
      var display  = stepper.querySelector('.cc-qty-input');
      if (!minusBtn || !plusBtn || !display) return;

      function getVal()  { return Math.max(1, parseInt(display.value, 10) || 1); }

      function sync(val) {
        val = Math.max(1, Math.min(999, val));
        display.value = val;
        minusBtn.disabled = (val <= 1);
        plusBtn.disabled  = (val >= 999);

        // Keep Shopify's native input in sync
        var native = getNativeQtyInput();
        if (native) {
          native.value = val;
          native.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      minusBtn.addEventListener('click', function () { sync(getVal() - 1); });
      plusBtn.addEventListener('click',  function () { sync(getVal() + 1); });
      display.addEventListener('change', function () { sync(getVal()); });
      display.addEventListener('blur',   function () { sync(getVal()); });

      sync(1);
    });
  }

  /* ─────────────────────────────────────────────
     2. CONTENT TABS
     ───────────────────────────────────────────── */
  function initTabs() {
    var tabBtns = document.querySelectorAll('.cc-tab-btn');
    if (!tabBtns.length) return;

    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // On mobile (≤600px) the accordion in product-extended.js handles this
        if (window.innerWidth <= 600) return;
        var target = btn.dataset.tab;
        document.querySelectorAll('.cc-tab-btn').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        document.querySelectorAll('.cc-tab-panel').forEach(function (p) {
          p.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        var panel = document.getElementById('cc-tab-' + target);
        if (panel) panel.classList.add('is-active');
      });
    });

    // Activate first tab
    tabBtns[0].click();
  }

  /* ─────────────────────────────────────────────
     3. NEXT WORKING DAY DELIVERY DATE
     Correct logic:
       - If current time >= 16:00, skip today (dispatch tomorrow)
       - Then find the next day that is Mon–Fri
     ───────────────────────────────────────────── */
  function calcNextWorkingDay() {
    var now    = new Date();
    var cutoff = new Date(now);
    cutoff.setHours(16, 0, 0, 0);

    // Start from tomorrow, or day after if already past cutoff
    var d = new Date(now);
    d.setDate(d.getDate() + (now >= cutoff ? 2 : 1));
    d.setHours(0, 0, 0, 0);

    // Advance past weekends
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }

    var days   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var months = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
    return days[d.getDay()] + ', ' + d.getDate() + ' ' + months[d.getMonth()];
  }

  function initDeliveryDate() {
    document.querySelectorAll('#cc-delivery-date').forEach(function (el) {
      el.textContent = calcNextWorkingDay();
    });
  }

  /* ─────────────────────────────────────────────
     4. INC VAT PRICE — reads raw cents from
        data-price-cents, multiplies by 1.2
     ───────────────────────────────────────────── */
  function initVatPrice() {
    document.querySelectorAll('.cc-price-ex[data-price-cents]').forEach(function (exEl) {
      var incEl = exEl.closest('.cc-buybox')
                   ? exEl.closest('.cc-buybox').querySelector('.cc-price-inc')
                   : null;
      if (!incEl) return;

      var cents  = parseFloat(exEl.dataset.priceCents);
      if (isNaN(cents)) return;

      var symbol = exEl.dataset.symbol || '£';
      var inc    = (cents * 1.2 / 100).toFixed(2);
      incEl.textContent = 'Inc. VAT: ' + symbol + inc;
    });
  }

  /* ─────────────────────────────────────────────
     5. MOBILE SWIPE CAROUSEL
     Uses pointer events (works mouse + touch).
     Falls back gracefully if no carousel found.
     ───────────────────────────────────────────── */
  function initCarousel() {
    var wrapper = document.querySelector('.cc-carousel-wrapper');
    if (!wrapper) return;

    var track  = wrapper.querySelector('.cc-carousel-track');
    var slides = wrapper.querySelectorAll('.cc-carousel-slide');
    var dots   = document.querySelectorAll('.cc-dot');
    if (!track || !slides.length) return;

    var current = 0;
    var startX  = 0;
    var dragging = false;

    function goTo(index) {
      current = Math.max(0, Math.min(slides.length - 1, index));
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === current);
      });
    }

    // Touch events
    wrapper.addEventListener('touchstart', function (e) {
      startX   = e.touches[0].clientX;
      dragging = true;
    }, { passive: true });

    wrapper.addEventListener('touchend', function (e) {
      if (!dragging) return;
      var diff = e.changedTouches[0].clientX - startX;
      if (diff < -40)       goTo(current + 1);
      else if (diff > 40)   goTo(current - 1);
      dragging = false;
    }, { passive: true });

    // Dot clicks
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); });
    });

    goTo(0);
  }

  /* ─────────────────────────────────────────────
     6. ADD TO BASKET — direct AJAX Cart API
     Uses /cart/add.js so we never depend on a
     hidden native form or its visibility state.
     ───────────────────────────────────────────── */
  function initAtbProxy() {
    document.querySelectorAll('.cc-atb-btn[data-proxy]').forEach(function (btn) {
      if (btn.dataset.ccInit) return;
      btn.dataset.ccInit = '1';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.disabled) return;

        var variantId = getSelectedVariantId();
        if (!variantId) {
          console.warn('[cc-atb] No variant ID — cannot add to cart');
          return;
        }

        var qtyEl  = document.querySelector('.cc-qty-input');
        var qty    = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
        var origText = btn.textContent;

        btn.disabled    = true;
        btn.textContent = 'ADDING…';

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        fetch(root + 'cart/add.js', {
          method:  'POST',
          headers: {
            'Content-Type':    'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty })
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            // Shopify returned an error (e.g. out-of-stock, sold out)
            btn.textContent = data.description || 'UNAVAILABLE';
            setTimeout(function () {
              btn.textContent = origText;
              btn.disabled    = false;
            }, 2500);
          } else {
            btn.textContent = '✓ ADDED TO BASKET';

            // Tell the theme to refresh its cart UI / bubble count
            document.dispatchEvent(new CustomEvent('cart:refresh',     { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:item-added',  { bubbles: true, detail: data }));

            // Ella-theme cart refresh (updates drawer + header count)
            fetch(root + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
                // Shopify theme event used by many themes for cart count
                document.dispatchEvent(new CustomEvent('product:added-to-cart', {
                  bubbles: true,
                  detail: { product: data, cart: cart }
                }));
              });

            setTimeout(function () {
              btn.textContent = origText;
              btn.disabled    = false;
            }, 2000);
          }
        })
        .catch(function () {
          btn.textContent = 'ERROR — RETRY';
          setTimeout(function () {
            btn.textContent = origText;
            btn.disabled    = false;
          }, 2500);
        });
      });
    });
  }

  /* ─────────────────────────────────────────────
     7. WISHLIST — localStorage toggle
     Tries the theme's native wishlist button
     first (broad selector); falls back to our
     own localStorage list with visual feedback.
     ───────────────────────────────────────────── */
  function initWishlistProxy() {
    var LS_KEY  = 'cc_wishlist';
    var wishlist = JSON.parse(localStorage.getItem(LS_KEY) || '[]');

    document.querySelectorAll('.cc-wishlist-btn').forEach(function (btn) {
      if (btn.dataset.ccInit) return;
      btn.dataset.ccInit = '1';

      var pid = String(btn.dataset.productId || '');

      // Restore saved state on page load
      if (pid && wishlist.indexOf(pid) !== -1) {
        applyWishlistState(btn, true);
      }

      btn.addEventListener('click', function () {
        // Try native theme wishlist button first (many possible selectors)
        var themeBtn = document.querySelector(
          '[data-wishlist-add], [data-wishlist-icon], [wishlist-icon],' +
          'button[is="wishlist-button"], wishlist-button,' +
          '[data-wishlist], .wishlist-button, button.card-wishlist,' +
          '.btn-wishlist, [data-action="wishlist"], [data-wishlist-handle]'
        );
        if (themeBtn && themeBtn !== btn) {
          themeBtn.click();
          return;
        }

        // localStorage fallback
        if (!pid) return;
        wishlist = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
        var idx   = wishlist.indexOf(pid);
        var added = idx === -1;

        if (added) {
          wishlist.push(pid);
        } else {
          wishlist.splice(idx, 1);
        }

        localStorage.setItem(LS_KEY, JSON.stringify(wishlist));
        applyWishlistState(btn, added);

        document.dispatchEvent(new CustomEvent('cc:wishlist:changed', {
          bubbles: true,
          detail:  { wishlist: wishlist, productId: pid, added: added }
        }));
      });
    });
  }

  /* Apply / remove wishlisted visual state on a button */
  function applyWishlistState(btn, wishlisted) {
    var svgPath = btn.querySelector('svg path');
    if (wishlisted) {
      btn.classList.add('is-wishlisted');
      btn.setAttribute('aria-pressed', 'true');
      if (svgPath) svgPath.setAttribute('fill', 'currentColor');
    } else {
      btn.classList.remove('is-wishlisted');
      btn.setAttribute('aria-pressed', 'false');
      if (svgPath) svgPath.setAttribute('fill', 'none');
    }
  }

  /* ─────────────────────────────────────────────
     INIT
     ───────────────────────────────────────────── */
  function init() {
    initQtyStepper();
    initTabs();
    initDeliveryDate();
    initVatPrice();
    initCarousel();
    initAtbProxy();
    initWishlistProxy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
